<template>
  <div class="txt-black">
    <div
      class="rounded-lg p-3 shadow-2xl bg-white mb-2 cursor-pointer hover:bg-gray-100 transition-all duration-200"
      @click="handleOpenHistory"
      role="button"
    >
      <div class="flex gap-4 items-center">
        <div>
          <div class="fs-20 fw-500 ml-1">Sổ khám bệnh</div>
          <div class="fs-13 txt-grey-600 ml-1">Xem lịch sử khám của bệnh nhân</div>
        </div>
        <div class="ml-auto flex">
          <i class="el-icon-arrow-right fs-24 txt-pri"></i>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="showHistoryModal"
      title="Lịch sử khám"
      width="92%"
      top="40px"
      append-to-body
      :before-close="handleCloseHistory"
    >
      <div class="visit-history" v-loading="loadingVisits">
        <div class="visit-history-sidebar">
          <div class="font-semibold mb-2">Danh sách ngày khám</div>
          <div v-if="patientVisits.length" class="visit-history-list">
            <div
              v-for="visit in patientVisits"
              :key="visit.id"
              class="visit-history-item"
              :class="{ 'visit-history-item-active': selectedVisit && selectedVisit.id === visit.id }"
              role="button"
              @click="handleSelectVisit(visit)"
            >
              <div class="font-semibold">{{ formatVisitDate(visit.start_time) }}</div>
              <div class="fs-12 txt-grey-600">{{ getDoctorName(visit) }}</div>
            </div>
          </div>
          <div v-else class="text-center txt-grey-600 py-4">
            Không có lịch sử khám.
          </div>
          <div class="flex justify-center pt-2" v-if="meta && meta.total">
            <el-pagination
              small
              background
              layout="prev, pager, next"
              :page-size="Number(meta.page_size || 10)"
              :current-page.sync="currentPage"
              :total="meta.total"
              @current-change="handleChangePage"
              hide-on-single-page
            ></el-pagination>
          </div>
        </div>

        <div class="visit-history-content" v-loading="loadingDetail">
          <div v-if="selectedVisitDetail && selectedVisitDetail.id">
            <div class="visit-history-title">
              {{ formatVisitDate(selectedVisitDetail.start_time) }}
            </div>

            <HistorySection title="Phiếu khám">
              <ConsulationTempV2
                :patient-visit="selectedVisitDetail"
                @refresh="getSelectedVisitDetail"
              />
            </HistorySection>

            <HistorySection title="Đơn thuốc">
              <PrescriptionTempV2
                :patient-visit="selectedVisitDetail"
                @refresh="getSelectedVisitDetail"
              />
            </HistorySection>

            <HistorySection title="Xét nghiệm">
              <IndicationTempV2
                :name="$t('Xét nghiệm')"
                :type="INDICATION_TYPES.CODE.TEST_FORM"
                :consultation="selectedConsultation"
                :patient-visit="selectedVisitDetail"
                :disable-route-sync="true"
                @refresh="getSelectedVisitDetail"
              />
            </HistorySection>

            <HistorySection title="Chẩn đoán hình ảnh">
              <IndicationTempV2
                :name="$t('Chẩn đoán hình ảnh')"
                :type="INDICATION_TYPES.CODE.ULTRASOUND"
                :consultation="selectedConsultation"
                :patient-visit="selectedVisitDetail"
                :disable-route-sync="true"
                @refresh="getSelectedVisitDetail"
              />
            </HistorySection>

            <HistorySection title="Sinh học phân tử">
              <IndicationTempV2
                :name="$t('Sinh học phân tử')"
                :type="INDICATION_TYPES.CODE.MOLECULAR_BIOLOGY"
                :consultation="selectedConsultation"
                :patient-visit="selectedVisitDetail"
                :disable-route-sync="true"
                @refresh="getSelectedVisitDetail"
              />
            </HistorySection>

            <HistorySection title="Chỉ định khác">
              <IndicationTempV2
                :name="$t('Chỉ định khác')"
                :type="INDICATION_TYPES.CODE.OTHERS"
                :consultation="selectedConsultation"
                :patient-visit="selectedVisitDetail"
                :disable-route-sync="true"
                @refresh="getSelectedVisitDetail"
              />
            </HistorySection>
          </div>
          <div v-else class="visit-history-empty">
            Chọn một ngày khám để xem lịch sử.
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ConsulationTempV2 from '@/components/PatientVisitHealthRecord/ConsulationTempV2.vue'
import IndicationTempV2 from '@/components/PatientVisitHealthRecord/IndicationTempV2.vue'
import PrescriptionTempV2 from '@/components/PatientVisitHealthRecord/PrescriptionTempV2.vue'
import { INDICATION_TYPES } from '@/utils/constants'

const HistorySection = {
  name: 'HistorySection',
  props: {
    title: {
      type: String,
      required: true
    }
  },
  render (h) {
    return h('div', { class: 'history-section' }, [
      h('div', { class: 'history-section-title' }, this.title),
      this.$slots.default
    ])
  }
}

export default {
  name: 'CoverPage',
  components: {
    ConsulationTempV2,
    IndicationTempV2,
    PrescriptionTempV2,
    HistorySection
  },
  props: {
    person_id: Number,
    health_record_id: [Number, String],
    patientVisit: {
      type: Object
    }
  },
  data () {
    return {
      INDICATION_TYPES,
      showHistoryModal: false,
      loadingVisits: false,
      loadingDetail: false,
      patientVisits: [],
      selectedVisit: null,
      selectedVisitDetail: null,
      currentPage: 1,
      meta: {}
    }
  },
  computed: {
    currentPersonId () {
      return this.person_id || this.patientVisit?.person_id || this.patientVisit?.person?.id || null
    },
    currentVisitId () {
      return this.patientVisit?.id || null
    },
    currentHealthRecordId () {
      return (
        this.health_record_id ||
        this.patientVisit?.health_record_id ||
        this.patientVisit?.health_record?.id ||
        this.patientVisit?.appt?.consultation?.health_record_id ||
        null
      )
    },
    selectedConsultation () {
      return {
        id: this.selectedVisitDetail?.appt?.consultation?.id,
        patient_visit_id: this.selectedVisitDetail?.id,
        health_record_id:
          this.selectedVisitDetail?.appt?.consultation?.health_record_id ||
          this.selectedVisitDetail?.health_record_id ||
          this.currentHealthRecordId,
        indication_order: this.selectedVisitDetail?.indication_order || []
      }
    }
  },
  methods: {
    async handleOpenHistory () {
      this.showHistoryModal = true
      this.currentPage = 1
      await this.getPatientVisits()
    },
    handleCloseHistory () {
      this.showHistoryModal = false
      this.selectedVisit = null
      this.selectedVisitDetail = null
    },
    async handleChangePage (page) {
      this.currentPage = page
      await this.getPatientVisits()
    },
    async getPatientVisits () {
      if (!this.currentHealthRecordId && !this.currentPersonId) {
        this.patientVisits = []
        this.meta = {}
        return
      }

      this.loadingVisits = true
      try {
        const params = {
          page_num: this.currentPage,
          sort_by: 'start_time',
          order: 'desc'
        }

        if (this.currentHealthRecordId) {
          params.health_record_id = this.currentHealthRecordId
        } else {
          params.person_id = this.currentPersonId
        }

        const res = await this.$rf
          .getRequest('DoctorRequest')
          .getPatientVisitsV2(params)

        this.patientVisits = (res?.data?.data || []).filter(visit => {
          return Number(visit?.id) !== Number(this.currentVisitId)
        })
        this.meta = res?.data?.page || {}

        if (this.patientVisits.length) {
          await this.handleSelectVisit(this.patientVisits[0])
        } else {
          this.selectedVisit = null
          this.selectedVisitDetail = null
        }
      } catch (error) {
        console.log(error)
        this.patientVisits = []
        this.meta = {}
      } finally {
        this.loadingVisits = false
      }
    },
    async handleSelectVisit (visit) {
      if (!visit?.id) return
      this.selectedVisit = visit
      await this.getSelectedVisitDetail()
    },
    async getSelectedVisitDetail () {
      if (!this.selectedVisit?.id) return

      this.loadingDetail = true
      try {
        const res = await this.$rf
          .getRequest('DoctorRequest')
          .getDetailPatientVisit(this.selectedVisit.id)
        this.selectedVisitDetail = res?.data || null
      } catch (error) {
        console.log(error)
        this.selectedVisitDetail = null
      } finally {
        this.loadingDetail = false
      }
    },
    formatVisitDate (date) {
      return date ? window.moment(date).utc().format('HH:mm DD/MM/YYYY') : ''
    },
    getDoctorName (visit) {
      return (
        visit?.doctor?.User?.name ||
        visit?.doctor_ref_name ||
        'Chưa có bác sĩ'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.visit-history {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  min-height: 70vh;
}

.visit-history-sidebar {
  border-right: 1px solid #e5e7eb;
  padding-right: 12px;
}

.visit-history-list {
  max-height: 62vh;
  overflow-y: auto;
}

.visit-history-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  background: #fff;
}

.visit-history-item:hover {
  background: #eef4ff;
}

.visit-history-item-active {
  border-color: #20409b;
  border-left: 5px solid #20409b;
  background: #dbeafe;
}

.visit-history-content {
  max-height: 72vh;
  overflow-y: auto;
  padding-right: 4px;
}

.visit-history-title {
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 12px;
}

.visit-history-empty {
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
}

.history-section {
  margin-bottom: 16px;
}

.history-section-title {
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 6px;
}

@media (max-width: 768px) {
  .visit-history {
    grid-template-columns: 1fr;
  }

  .visit-history-sidebar {
    border-right: 0;
    border-bottom: 1px solid #e5e7eb;
    padding-right: 0;
    padding-bottom: 12px;
  }
}
</style>
